// context/BiensContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from 'axios';

export type Bien = {
  id: string;
  title: string;
  location: string;
  price: string;
  type: string;
  status: string;
  bedrooms: number; 
  bathrooms: number;
  area: string;
  date: string;
  description?: string;
  images?:PropertyImage[];
  isDraft?: boolean;
  createdAt: string;
  quartier?: string;
};
export type PropertyImage = {
  id: string; // ✅ L'identifiant de chaque image
  url: string; // ✅ L'URL de l'image
};
interface BiensContextType {
  biens: Bien[];
  loading: boolean;
  error: string | null;
  setBiens: React.Dispatch<React.SetStateAction<Bien[]>>;
  addBien: (bien: Bien) => void;
  removeBien: (id: string) => void;
  updateBien: (id: string, bien: Partial<Bien>) => void;
  getBienById: (id: string) => Bien | undefined;
  publishBien: (id: string) => void;
}

const BiensContext = createContext<BiensContextType | undefined>(undefined);

export const useBiens = () => {
  const context = useContext(BiensContext);
  if (!context) {
    throw new Error('useBiens must be used within a BiensProvider');
  }
  return context;
};

export const BiensProvider = ({ children }: { children: ReactNode }) => {
  const [biens, setBiens] = useState<Bien[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 🔁 Charger les biens depuis l'API
  useEffect(() => {
    axios.get('https://universelle.ma/api/biens')
      .then(response => {
        setBiens(response.data.data || []);
      })
      .catch(() => {
        setError("Erreur lors du chargement des biens");
      })
      .finally(() => setLoading(false));
  }, []);

  const addBien = (bien: Bien) => {
    setBiens(prev => [bien, ...prev]);
  };

  const removeBien = (id: string) => {
    setBiens(prev => prev.filter(bien => bien.id !== id));
  };

  const updateBien = (id: string, updated: Partial<Bien>) => {
    setBiens(prev =>
      prev.map(bien =>
        bien.id === id ? { ...bien, ...updated } : bien
      )
    );
  };

  const getBienById = (id: string) =>
    biens.find(bien => String(bien.id) === String(id));
  
const publishBien = async (id: string) => {
  const token = localStorage.getItem("access_token");
  const bien = getBienById(id);

  if (!token || !bien) {
    console.warn("Token ou bien introuvable");
    return;
  }

  try {
const payload: any = {
  title: bien.title,
  type: bien.type,
  status: bien.status,
  location: bien.location,
  price: bien.price,
  area: bien.area,
  bedrooms: String(bien.bedrooms ?? 0),
  bathrooms: String(bien.bathrooms ?? 0),
  description: bien.description ?? "Pas de description",
  is_draft: 0,
  is_featured: 0,
  quartier: bien.quartier ?? "",
};

// ✅ N'ajoute apartments QUE s'il y en a
if (bien.hasOwnProperty("apartments") && Array.isArray(bien.apartments) && bien.apartments.length > 0) {
  payload.apartments = bien.apartments;
}

console.log("🧾 Payload envoyé pour publication :", payload);

    await axios.put(
      `https://universelle.ma/api/biens/${id}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    updateBien(id, { isDraft: false });
  

  } catch (error) {
    console.error("Erreur de publication :", error);
  }
};
const draftBien = async (id: string) => {
  const token = localStorage.getItem("access_token");
  const bien = getBienById(id);

  if (!token || !bien) {
    console.warn("Token ou bien introuvable");
    return;
  }

  try {
    const payload: any = {
      title: bien.title,
      type: bien.type,
      status: bien.status,
      location: bien.location,
      price: bien.price,
      area: bien.area,
      bedrooms: String(bien.bedrooms ?? 0),
      bathrooms: String(bien.bathrooms ?? 0),
      description: bien.description ?? "Pas de description",
      is_draft: 1, // 🟡 Mettre en brouillon ici
      is_featured: 0,
      quartier: bien.quartier ?? "",
    };

    // Ajouter apartments si nécessaire
    if (
      bien.hasOwnProperty("apartments") &&
      Array.isArray(bien.apartments) &&
      bien.apartments.length > 0
    ) {
      payload.apartments = bien.apartments;
    }

    console.log("📝 Payload pour mise en brouillon :", payload);

    await axios.put(
      `https://universelle.ma/api/biens/${id}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    updateBien(id, { isDraft: true }); // ✅ MAJ locale

    console.log(`✅ Bien ${id} mis en brouillon avec succès`);
  } catch (error) {
    console.error("Erreur lors de la mise en brouillon :", error);
  }
};



  return (
    <BiensContext.Provider value={{ biens, loading, error, setBiens, addBien, removeBien, updateBien, getBienById, publishBien,draftBien, }}>
      {children}
    </BiensContext.Provider>
  );
};
